// React contexts that wire the action runner into the page designer.
//
// Three independent contexts so unrelated subtrees only re-render when their
// own state changes:
//   - PageRuntimeContext: imperative API for buttons (run actions, set inputs,
//     focus, etc.) and per-action state lookup.
//   - PageFieldErrorsContext: per-input error message map; read by InputRenderer.
//   - PageFieldRefsContext: input-name → DOM element registry; populated by
//     InputRenderer via callback ref, read by the runner for focus_input.

import { createContext, useContext } from 'react'
import type { PageAction, PageInput, Section } from '@twasta/page-schema'
import type { FormStoreSubmission } from '../forms'
import type { VarSource } from './substitute'

// ─── Action state (per named action) ────────────────────────────────

export interface ActionState {
  loading: boolean
  error?: string                 // normalized message
  response?: unknown             // parsed JSON or raw text
  status?: number                // HTTP status if available
}

// ─── Runtime API exposed to PageButton ──────────────────────────────

export interface PageRuntime {
  // Lookup/run named actions. The button passes the action *name*; the
  // runtime resolves it against the current page model.
  // `scope` is the variables at the click — inside a repeat, the clicked
  // ROW — and resolves BEFORE page variables in the action's `{{…}}` tokens.
  // `extra` adds a few named values of its own (`{{drop.lane}}`, `{{new_rows}}`).
  runActionByName: (name: string, scope?: VarSource, extra?: Record<string, unknown>) => Promise<void>

  // Read-only per-action state (used to disable buttons while loading).
  getActionState: (name: string) => ActionState | undefined

  // Inline ButtonClick handlers (set_inputs, navigate, external_url) route
  // through these helpers so the runner remains the single source of truth.
  setInput: (name: string, value: string) => void
  clearInputs: (names?: string[]) => void
  navigateTo: (objType: string, objName: string) => void

  // Useful for designer-mode toasts.
  showMessage: (level: 'success' | 'error' | 'info', text: string) => void

  // Snapshot accessors (used by the runner internally; rarely from buttons).
  getInputs: () => PageInput[]
  getInputValues: () => Record<string, unknown>
  getActions: () => PageAction[]
  // Current project name (used by editors that load destination metadata
  // — e.g. the navigate editor's argument target dropdown). Empty string
  // when invoked outside a designer (e.g. tests/storybook).
  getProject: () => string
  // The page's sections, for editors that need to enumerate real targets on
  // the current page — the `scroll_to` anchor picker and the `open_popup`
  // section picker both offer a dropdown of what actually exists rather than
  // asking the author to type an id and hope. Empty outside a designer.
  getSections?: () => Section[]
  // POSTs a form / newsletter / contact-form submission to the built-in
  // submissions store (which also runs the block's server-side destination —
  // an email, a saved record). Supplied by each host, since only the host
  // knows its API and how the page is addressed; absent on the designer
  // canvas, where submitting stores nothing.
  submitForm?: (payload: FormStoreSubmission) => Promise<void>
}

// Default no-op runtime keeps the contexts safe to consume even if a renderer
// is mounted outside a PageDesignerPage (e.g. in storybook/tests).
const NOOP_RUNTIME: PageRuntime = {
  runActionByName: async () => {},
  getActionState: () => undefined,
  setInput: () => {},
  clearInputs: () => {},
  navigateTo: () => {},
  showMessage: () => {},
  getInputs: () => [],
  getInputValues: () => ({}),
  getActions: () => [],
  getProject: () => '',
  getSections: () => [],
}

export const PageRuntimeContext = createContext<PageRuntime>(NOOP_RUNTIME)
export function usePageRuntime(): PageRuntime { return useContext(PageRuntimeContext) }

// ─── Field errors / focus contexts ──────────────────────────────────

export type FieldErrors = Record<string, string>

export interface FieldErrorsApi {
  errors: FieldErrors
  setError: (name: string, message: string) => void
  clearError: (name: string) => void
  clearAll: () => void
}

const NOOP_FIELD_ERRORS: FieldErrorsApi = {
  errors: {},
  setError: () => {},
  clearError: () => {},
  clearAll: () => {},
}

export const PageFieldErrorsContext = createContext<FieldErrorsApi>(NOOP_FIELD_ERRORS)
export function usePageFieldErrors(): FieldErrorsApi { return useContext(PageFieldErrorsContext) }

// Field refs are stored in a single shared Map (mutated in place). The Map
// itself is wrapped in a stable provider value so the context only changes
// reference once at mount.
export interface FieldRefsApi {
  register: (name: string, el: HTMLElement | null) => void
  focus: (name: string) => boolean       // returns true if focused, false if not mounted
}

const NOOP_FIELD_REFS: FieldRefsApi = {
  register: () => {},
  focus: () => false,
}

export const PageFieldRefsContext = createContext<FieldRefsApi>(NOOP_FIELD_REFS)
export function usePageFieldRefs(): FieldRefsApi { return useContext(PageFieldRefsContext) }
